/**
 * Loads the Sentry SDK after first paint instead of in the entry bundle.
 *
 * The SDK (about 250 KB raw) used to be statically imported by src/instrument.ts and a
 * dozen call sites, so every visitor parsed it before the page could respond (mobile TBT
 * 2.2-2.7 s). Call sites now import this module, which exposes the same function names:
 * a call made before the SDK has loaded is queued and replayed once it has. The init
 * options, beforeSend filter and PII scrubbing stay untouched in src/instrument.ts.
 *
 * Errors thrown before the SDK loads are caught by two early window listeners and queued
 * (as captureException) until the SDK's own global handlers take over.
 */
type Sdk = typeof import('@sentry/react');
type Job = (sdk: Sdk) => void;

const MAX_QUEUE = 100;
let sdk: Sdk | null = null;
let started = false;
const queue: Job[] = [];

function run(job: Job) {
  if (sdk) {
    job(sdk);
  } else if (queue.length < MAX_QUEUE) {
    queue.push(job);
  }
}

export function captureException(...args: Parameters<Sdk['captureException']>) {
  run((s) => { s.captureException(...args); });
}
export function captureMessage(...args: Parameters<Sdk['captureMessage']>) {
  run((s) => { s.captureMessage(...args); });
}
export function addBreadcrumb(...args: Parameters<Sdk['addBreadcrumb']>) {
  run((s) => { s.addBreadcrumb(...args); });
}
export function setTag(...args: Parameters<Sdk['setTag']>) {
  run((s) => { s.setTag(...args); });
}
export function setUser(...args: Parameters<Sdk['setUser']>) {
  run((s) => { s.setUser(...args); });
}
export function withScope(callback: (scope: import('@sentry/react').Scope) => void) {
  run((s) => { s.withScope(callback); });
}

/** Test seam: true once the SDK is loaded and the queue is flushed. */
export function isSentryLoaded() {
  return sdk !== null;
}

/**
 * Load and initialise the SDK now. Safe to call more than once. Resolves after the queue
 * has been replayed.
 */
export async function loadSentry(): Promise<void> {
  if (sdk) return;
  // instrument.ts runs Sentry.init on import; the SDK module is shared with it.
  const [, loaded] = await Promise.all([import('@/instrument'), import('@sentry/react')]);
  sdk = loaded;
  for (const job of queue.splice(0)) {
    try { job(loaded); } catch { /* a queued call must never break the page */ }
  }
}

/** Start capturing early errors and schedule the SDK load for after first paint. */
export function startSentry(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  const onError = (e: ErrorEvent) => { captureException(e.error ?? e.message); };
  const onRejection = (e: PromiseRejectionEvent) => { captureException(e.reason); };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  const go = () => {
    loadSentry()
      .catch(() => { /* blocked or offline: the queue stays capped and the app keeps working */ })
      .finally(() => {
        if (sdk) {
          window.removeEventListener('error', onError);
          window.removeEventListener('unhandledrejection', onRejection);
        }
      });
  };
  const schedule = () => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) ric(go, { timeout: 3000 });
    else setTimeout(go, 1500);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}
